import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * @typedef {Object} CartMutationQueueEntry
 * @property {() => Promise<unknown>} task
 * @property {string | undefined} coalesceKey
 * @property {(value: unknown) => void} resolve
 * @property {(reason?: unknown) => void} reject
 * @property {Promise<unknown>} promise
 */

/**
 * @typedef {Object} CartMutationController
 * @property {<T>(task: () => Promise<T>, options?: { coalesceKey?: string }) => Promise<T>} enqueue
 */

/**
 * @typedef {Object} CartLineMutation
 * @property {string} key
 * @property {number} quantity
 * @property {'change' | 'clear'} action
 * @property {number} confirmedQuantity
 * @property {HTMLTableRowElement} row
 * @property {CartUndoItem | null} undoItem
 */

/**
 * @typedef {Object} CartUndoItem
 * @property {string} id
 * @property {number} quantity
 * @property {Record<string, unknown>} [properties]
 * @property {string} [selling_plan]
 */

/**
 * @typedef {Object} CartUndoState
 * @property {CartUndoItem} item
 * @property {string} title
 * @property {number} expiresAt
 * @property {number | undefined} timeoutId
 * @property {boolean} [inFlight]
 */

const CART_MUTATION_CONTROLLER_KEY = 'PPCartMutations';
const CART_UNDO_STATE_KEY = '__ppCartUndoState';
const QUANTITY_DEBOUNCE_TIME = 300;
const CART_CONFLICT_RETRY_DELAY = 250;
const UNDO_WINDOW = 5000;

/**
 * Returns the shared cart mutation queue. Other cart features can join the same queue through
 * `window.PPCartMutations.enqueue(task, { coalesceKey })`.
 *
 * @returns {CartMutationController}
 */
function getCartMutationController() {
  const scope = /** @type {Window & Record<string, unknown>} */ (window);
  const existingController = /** @type {CartMutationController | undefined} */ (
    scope[CART_MUTATION_CONTROLLER_KEY]
  );

  if (typeof existingController?.enqueue === 'function') return existingController;

  /** @type {CartMutationQueueEntry[]} */
  const queue = [];
  /** @type {Map<string, CartMutationQueueEntry>} */
  const queuedByKey = new Map();
  let isRunning = false;

  const drain = async () => {
    if (isRunning) return;
    isRunning = true;

    while (queue.length > 0) {
      const entry = queue.shift();
      if (!entry) continue;

      if (entry.coalesceKey && queuedByKey.get(entry.coalesceKey) === entry) {
        queuedByKey.delete(entry.coalesceKey);
      }

      try {
        entry.resolve(await entry.task());
      } catch (error) {
        entry.reject(error);
      }
    }

    isRunning = false;
  };

  const controller = /** @type {CartMutationController} */ ({
    enqueue(task, options = {}) {
      const { coalesceKey } = options;
      const queuedEntry = coalesceKey ? queuedByKey.get(coalesceKey) : undefined;

      if (queuedEntry) {
        queuedEntry.task = task;
        return /** @type {Promise<ReturnType<typeof task>>} */ (queuedEntry.promise);
      }

      /** @type {(value: unknown) => void} */
      let resolve = () => {};
      /** @type {(reason?: unknown) => void} */
      let reject = () => {};
      const promise = new Promise((resolvePromise, rejectPromise) => {
        resolve = resolvePromise;
        reject = rejectPromise;
      });
      const entry = { task, coalesceKey, resolve, reject, promise };

      queue.push(entry);
      if (coalesceKey) queuedByKey.set(coalesceKey, entry);
      void drain();

      return /** @type {Promise<ReturnType<typeof task>>} */ (promise);
    },
  });

  scope[CART_MUTATION_CONTROLLER_KEY] = controller;
  return controller;
}

/**
 * Fetches a cart JSON endpoint and retries one Shopify concurrency conflict.
 *
 * @param {string} url
 * @param {RequestInit} config
 * @returns {Promise<Record<string, any>>}
 */
async function fetchCartJSON(url, config) {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const response = await fetch(url, config);
    let data;

    try {
      data = await response.json();
    } catch {
      throw new Error('The cart returned an invalid response. Please try again.');
    }

    if (response.status === 409 && attempt === 0) {
      await new Promise((resolve) => window.setTimeout(resolve, CART_CONFLICT_RETRY_DELAY));
      continue;
    }

    if (!response.ok || data.errors || data.status >= 400) {
      const error = new Error(getCartErrorMessage(data));
      Object.assign(error, { status: response.status || data.status || 0 });
      throw error;
    }

    return data;
  }

  throw Object.assign(new Error('The cart is busy. Please try again.'), { status: 409 });
}

/**
 * @param {Record<string, any>} data
 * @returns {string}
 */
function getCartErrorMessage(data) {
  if (typeof data.errors === 'string') return data.errors;
  if (typeof data.message === 'string') return data.message;
  if (data.errors && typeof data.errors === 'object') return Object.values(data.errors).flat().join(' ');
  return "We couldn't update your cart. Please try again.";
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isRetryableError(error) {
  if (!(error instanceof Error)) return true;
  const status = Number(/** @type {Error & { status?: number }} */ (error).status || 0);
  return status === 0 || status === 409 || status >= 500;
}

/**
 * @param {unknown} error
 * @param {string} fallback
 * @returns {string}
 */
function getReadableMutationError(error, fallback) {
  if (error instanceof TypeError) return "We couldn't connect to the store. Check your connection and try again.";
  return error instanceof Error && error.message ? error.message : fallback;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /** @type {Map<string, number>} */
  #quantityTimers = new Map();

  /** @type {Map<string, CartLineMutation>} */
  #retryableMutations = new Map();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
    queueMicrotask(() => this.showUndoState());
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event. Debouncing is keyed by the immutable cart line
   * key so edits to separate products cannot overwrite one another.
   *
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange = (event) => {
    const row = this.#getEventRow(event);
    if (!row) return;

    const key = row.dataset.key;
    const quantity = Number(event.detail.quantity);
    if (!key || !Number.isFinite(quantity) || quantity < 0) return;

    const existingTimer = this.#quantityTimers.get(key);
    if (existingTimer) window.clearTimeout(existingTimer);

    const mutation = this.#createLineMutation(row, quantity, quantity === 0 ? 'clear' : 'change');
    if (!mutation) return;

    if (quantity === 0) {
      this.#quantityTimers.delete(key);
      void this.#queueLineMutation(mutation);
      return;
    }

    const textComponent = /** @type {TextComponent | null} */ (row.querySelector('text-component'));
    textComponent?.shimmer();

    const timer = window.setTimeout(() => {
      this.#quantityTimers.delete(key);
      void this.#queueLineMutation(mutation);
    }, QUANTITY_DEBOUNCE_TIME);
    this.#quantityTimers.set(key, timer);
  };

  /**
   * Handles the line item removal without deleting the confirmed row optimistically.
   *
   * @param {Event} event
   */
  onLineItemRemove(event) {
    event.preventDefault();
    const row = this.#getEventRow(event);
    if (!row) return;

    const key = row.dataset.key;
    if (!key) return;

    const existingTimer = this.#quantityTimers.get(key);
    if (existingTimer) window.clearTimeout(existingTimer);
    this.#quantityTimers.delete(key);

    const mutation = this.#createLineMutation(row, 0, 'clear');
    if (mutation) void this.#queueLineMutation(mutation);
  }

  /**
   * Retries the last transient mutation for a line.
   *
   * @param {Event} event
   */
  retryLineItem(event) {
    event.preventDefault();
    const row = this.#getEventRow(event);
    const key = row?.dataset.key;
    if (!row || !key) return;

    const mutation =
      this.#retryableMutations.get(key) ||
      this.#createLineMutation(
        row,
        Number(row.dataset.cartRetryQuantity),
        row.dataset.cartRetryAction === 'clear' ? 'clear' : 'change'
      );
    if (!mutation) return;

    mutation.row = row;
    void this.#queueLineMutation(mutation);
  }

  /**
   * Restores the most recently removed standard line during the five-second undo window.
   *
   * @param {Event} event
   */
  undoLastRemoval(event) {
    event.preventDefault();
    const undoState = getCartUndoState();
    if (!undoState || undoState.expiresAt <= Date.now()) {
      clearCartUndoState();
      this.#hideUndoState();
      return;
    }
    if (undoState.inFlight) return;
    undoState.inFlight = true;

    const button = event.target instanceof HTMLButtonElement ? event.target : null;
    if (button) button.disabled = true;
    this.#setCartStatus(`Restoring ${undoState.title}…`);

    void getCartMutationController()
      .enqueue(() => this.#restoreRemovedItem(undoState), { coalesceKey: 'undo-removal' })
      .catch((error) => {
        console.error(error);
        const message = getReadableMutationError(error, "We couldn't restore this item. Please try again.");
        this.#setCartStatus(message);
        undoState.inFlight = false;
        undoState.expiresAt = Date.now() + UNDO_WINDOW;
        setCartUndoState(undoState);
        scheduleUndoExpiry(undoState);
        if (button) button.disabled = false;
        document.dispatchEvent(new CartErrorEvent(this.id, message));
      });
  }

  /**
   * Makes the current undo state visible after a section morph or component connection.
   */
  showUndoState() {
    const undoState = getCartUndoState();
    if (!undoState || undoState.expiresAt <= Date.now()) {
      this.#hideUndoState();
      return;
    }

    const recovery = this.querySelector('[data-cart-recovery]');
    const button = recovery?.querySelector('[data-cart-undo]');
    if (!(recovery instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) return;

    recovery.classList.remove('hidden');
    button.hidden = false;
    button.disabled = Boolean(undoState.inFlight);
    this.#setCartStatus(`${undoState.title} removed.`);
  }

  /**
   * Clears the recovery announcement when an undo window expires.
   */
  clearRecoveryStatus() {
    this.#setCartStatus('');
    this.#hideUndoState();
  }

  /**
   * @param {HTMLTableRowElement} row
   * @param {number} quantity
   * @param {'change' | 'clear'} action
   * @returns {CartLineMutation | null}
   */
  #createLineMutation(row, quantity, action) {
    const key = row.dataset.key;
    if (!key || !Number.isFinite(quantity) || quantity < 0) return null;

    return {
      key,
      quantity,
      action,
      confirmedQuantity: Number(row.dataset.quantity || 0),
      row,
      undoItem: action === 'clear' ? this.#getUndoItem(row) : null,
    };
  }

  /**
   * @param {CartLineMutation} mutation
   */
  async #queueLineMutation(mutation) {
    this.#retryableMutations.delete(mutation.key);
    this.#clearLineError(mutation.row);
    this.#setLineBusy(mutation.row, true, mutation.action === 'clear' ? 'Removing…' : 'Updating…');

    const marker = cartPerformance.createStartingMarker(`${mutation.action}:user-action`);

    try {
      await getCartMutationController().enqueue(() => this.#performLineMutation(mutation), {
        coalesceKey: `line:${mutation.key}`,
      });
    } catch (error) {
      console.error(error);
      resetShimmer(this);

      const currentRow = this.#findRow(mutation.key) || mutation.row;
      this.#setLineBusy(currentRow, false, '');
      this.#restoreConfirmedQuantity(currentRow, mutation.confirmedQuantity);

      const message = getReadableMutationError(error, "We couldn't update this item. Please try again.");
      const retryable = isRetryableError(error);
      if (retryable) this.#retryableMutations.set(mutation.key, mutation);
      this.#showLineError(currentRow, message, retryable, mutation);
      document.dispatchEvent(new CartErrorEvent(this.id, message));
    } finally {
      cartPerformance.measureFromMarker(marker);
    }
  }

  /**
   * @param {CartLineMutation} mutation
   */
  async #performLineMutation(mutation) {
    const sections = this.#getSectionsToUpdate();
    const body = JSON.stringify({
      id: mutation.key,
      quantity: mutation.quantity,
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    const data = await fetchCartJSON(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const sectionHtml = data.sections?.[this.sectionId];
    if (typeof sectionHtml !== 'string') throw new Error("We couldn't refresh your cart. Please try again.");

    resetShimmer(this);
    const itemCount = this.#getItemCount(sectionHtml, data.item_count);
    const updateEvent = new CartUpdateEvent(data, this.sectionId, {
      itemCount,
      source: 'cart-items-component',
      action: mutation.action,
      sections: data.sections,
    });

    if (this.isConnected) {
      this.dispatchEvent(updateEvent);
      await this.#morphOrRefreshSection(sectionHtml);
    } else {
      document.dispatchEvent(updateEvent);
    }

    this.#retryableMutations.delete(mutation.key);

    if (mutation.action === 'clear' && mutation.undoItem) {
      const title = mutation.row.dataset.title || 'Item';
      const undoState = { item: mutation.undoItem, title, expiresAt: Date.now() + UNDO_WINDOW, timeoutId: undefined };
      setCartUndoState(undoState);
      scheduleUndoExpiry(undoState);
      queueMicrotask(() => {
        document.querySelectorAll('cart-items-component').forEach((component) => {
          if (component instanceof CartItemsComponent) component.showUndoState();
        });
      });
    } else {
      this.#announceCartStatus('Cart updated.');
    }
  }

  /**
   * @param {CartUndoState} undoState
   */
  async #restoreRemovedItem(undoState) {
    const sections = this.#getSectionsToUpdate();
    const body = JSON.stringify({
      items: [undoState.item],
      sections: sections.join(','),
      sections_url: window.location.pathname,
    });
    const data = await fetchCartJSON(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const sectionHtml = data.sections?.[this.sectionId];
    if (typeof sectionHtml !== 'string') throw new Error("We couldn't refresh your cart. Please try again.");

    clearCartUndoState();
    const itemCount = this.#getItemCount(sectionHtml, data.item_count);
    const updateEvent = new CartUpdateEvent(data, this.sectionId, {
      itemCount,
      source: 'cart-undo',
      variantId: undoState.item.id,
      sections: data.sections,
    });

    if (this.isConnected) {
      this.dispatchEvent(updateEvent);
      await this.#morphOrRefreshSection(sectionHtml);
    } else {
      document.dispatchEvent(updateEvent);
    }

    queueMicrotask(() => {
      document.querySelectorAll('cart-items-component').forEach((component) => {
        if (component instanceof CartItemsComponent) {
          component.clearRecoveryStatus();
          component.#announceCartStatus(`${undoState.title} restored.`);
        }
      });
    });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles cart updates from other components.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      void sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    if (cartItemsHtml) {
      void morphSection(this.sectionId, cartItemsHtml).catch(() =>
        sectionRenderer.renderSection(this.sectionId, { cache: false })
      );
    } else {
      void sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * @param {Event} event
   * @returns {HTMLTableRowElement | null}
   */
  #getEventRow(event) {
    if (!(event.target instanceof Element)) return null;
    const row = event.target.closest('[data-key]');
    return row instanceof HTMLTableRowElement && this.contains(row) ? row : null;
  }

  /**
   * @param {string} key
   * @returns {HTMLTableRowElement | null}
   */
  #findRow(key) {
    const findIn = (root) =>
      Array.from(root.querySelectorAll('[data-key]')).find(
        (row) => row instanceof HTMLTableRowElement && row.dataset.key === key
      ) || null;
    const localRow = findIn(this);
    if (this.isConnected && localRow) return localRow;

    const currentComponent = Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).find(
      (component) => component instanceof HTMLElement && component.dataset.sectionId === this.sectionId
    );
    return currentComponent ? findIn(currentComponent) : localRow;
  }

  /**
   * @param {HTMLTableRowElement} row
   * @returns {CartUndoItem | null}
   */
  #getUndoItem(row) {
    if (row.dataset.undoEligible !== 'true' || !row.dataset.variantId) return null;

    /** @type {CartUndoItem} */
    const item = {
      id: row.dataset.variantId,
      quantity: Number(row.dataset.quantity || 1),
    };

    if (row.dataset.properties) {
      try {
        const properties = JSON.parse(row.dataset.properties);
        if (properties && Object.keys(properties).length > 0) item.properties = properties;
      } catch {
        return null;
      }
    }

    if (row.dataset.sellingPlanId) item.selling_plan = row.dataset.sellingPlanId;
    return item;
  }

  /**
   * @returns {string[]}
   */
  #getSectionsToUpdate() {
    const sectionIds = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
      if (component instanceof HTMLElement && component.dataset.sectionId) sectionIds.add(component.dataset.sectionId);
    });
    return Array.from(sectionIds);
  }

  /**
   * A cart mutation has already succeeded when this runs, so a morph failure must refresh the UI
   * rather than expose a retry that could repeat an add operation.
   *
   * @param {string} sectionHtml
   */
  async #morphOrRefreshSection(sectionHtml) {
    try {
      await morphSection(this.sectionId, sectionHtml);
    } catch (error) {
      console.error(error);
      try {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      } catch (refreshError) {
        console.error(refreshError);
        this.#announceCartStatus('Cart updated. Refresh the page to see the latest cart.');
      }
    }
  }

  /**
   * @param {string} sectionHtml
   * @param {number | undefined} fallback
   * @returns {number}
   */
  #getItemCount(sectionHtml, fallback) {
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const hiddenCount = parsed.querySelector('[ref="cartItemCount"]')?.textContent;
    return hiddenCount ? Number.parseInt(hiddenCount, 10) : Number(fallback || 0);
  }

  /**
   * @param {HTMLTableRowElement} row
   * @param {boolean} busy
   * @param {string} message
   */
  #setLineBusy(row, busy, message) {
    row.setAttribute('aria-busy', String(busy));
    row.classList.toggle('cart-items__table-row--pending', busy);

    row.querySelectorAll('button, input').forEach((control) => {
      if (!(control instanceof HTMLButtonElement || control instanceof HTMLInputElement)) return;

      if (busy) {
        control.dataset.cartWasDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.dataset.cartWasDisabled !== undefined) {
        control.disabled = control.dataset.cartWasDisabled === 'true';
        delete control.dataset.cartWasDisabled;
      }
    });

    const status = row.querySelector('[data-cart-line-status]');
    if (status instanceof HTMLElement) status.textContent = message;
  }

  /**
   * @param {HTMLTableRowElement} row
   * @param {number} quantity
   */
  #restoreConfirmedQuantity(row, quantity) {
    const input = row.querySelector('quantity-selector-component input');
    if (!(input instanceof HTMLInputElement)) return;
    input.value = String(quantity);
    input.defaultValue = String(quantity);
  }

  /**
   * @param {HTMLTableRowElement} row
   * @param {string} message
   * @param {boolean} retryable
   * @param {CartLineMutation} mutation
   */
  #showLineError(row, message, retryable, mutation) {
    const container = row.querySelector('[data-cart-line-error]');
    const text = container?.querySelector('[data-cart-line-error-text]');
    const retry = container?.querySelector('[data-cart-line-retry]');

    if (container instanceof HTMLElement) container.classList.remove('hidden');
    if (text instanceof HTMLElement) text.textContent = message;
    if (retry instanceof HTMLButtonElement) retry.hidden = !retryable;
    if (retryable) {
      row.dataset.cartRetryQuantity = String(mutation.quantity);
      row.dataset.cartRetryAction = mutation.action;
    }
  }

  /**
   * @param {HTMLTableRowElement} row
   */
  #clearLineError(row) {
    const container = row.querySelector('[data-cart-line-error]');
    const text = container?.querySelector('[data-cart-line-error-text]');
    const retry = container?.querySelector('[data-cart-line-retry]');

    if (container instanceof HTMLElement) container.classList.add('hidden');
    if (text instanceof HTMLElement) text.textContent = '';
    if (retry instanceof HTMLButtonElement) retry.hidden = true;
    delete row.dataset.cartRetryQuantity;
    delete row.dataset.cartRetryAction;
  }

  /**
   * @param {string} message
   */
  #setCartStatus(message) {
    const recovery = this.querySelector('[data-cart-recovery]');
    const status = recovery?.querySelector('[data-cart-status]');
    if (!(recovery instanceof HTMLElement) || !(status instanceof HTMLElement)) return;

    recovery.classList.remove('hidden');
    status.textContent = message;
  }

  /**
   * @param {string} message
   */
  #announceCartStatus(message) {
    const status = this.querySelector('[data-cart-live-status]');
    if (status instanceof HTMLElement) status.textContent = message;
  }

  #hideUndoState() {
    const recovery = this.querySelector('[data-cart-recovery]');
    const button = recovery?.querySelector('[data-cart-undo]');
    if (button instanceof HTMLButtonElement) button.hidden = true;
    if (recovery instanceof HTMLElement && !recovery.querySelector('[data-cart-status]')?.textContent) {
      recovery.classList.add('hidden');
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

/**
 * @returns {CartUndoState | null}
 */
function getCartUndoState() {
  const scope = /** @type {Window & Record<string, unknown>} */ (window);
  return /** @type {CartUndoState | null} */ (scope[CART_UNDO_STATE_KEY] || null);
}

/**
 * @param {CartUndoState} state
 */
function setCartUndoState(state) {
  const scope = /** @type {Window & Record<string, unknown>} */ (window);
  const previousState = getCartUndoState();
  if (previousState?.timeoutId) window.clearTimeout(previousState.timeoutId);
  scope[CART_UNDO_STATE_KEY] = state;
}

function clearCartUndoState() {
  const scope = /** @type {Window & Record<string, unknown>} */ (window);
  const state = getCartUndoState();
  if (state?.timeoutId) window.clearTimeout(state.timeoutId);
  delete scope[CART_UNDO_STATE_KEY];
}

/**
 * @param {CartUndoState} state
 */
function scheduleUndoExpiry(state) {
  state.timeoutId = window.setTimeout(() => {
    if (getCartUndoState() !== state) return;
    clearCartUndoState();
    document.querySelectorAll('cart-items-component').forEach((component) => {
      if (component instanceof CartItemsComponent) {
        component.clearRecoveryStatus();
      }
    });
  }, Math.max(0, state.expiresAt - Date.now()));
}

// Expose the shared queue as soon as the cart module loads so notes, discounts,
// and in-cart recommendations serialize with line-item requests from the start.
getCartMutationController();

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
